/**
 * ML Prediction Field Categories
 * Complete mapping of 289 header fields and 216 position fields
 * from the Atlas ML Predictor model to ATLAS form paths
 *
 * Generated from atlas_vocab.pkl vocabulary file
 */

// ============================================================================
// CONFIDENCE THRESHOLDS (AUDIT FIX: Consolidated from multiple files)
// ============================================================================

/** High confidence threshold (80%+) - predictions are very reliable */
export const HIGH_CONFIDENCE = 0.8;

/** Medium confidence threshold (60-80%) - predictions need review */
export const MEDIUM_CONFIDENCE = 0.6;

/** Low confidence threshold (<60%) - predictions are uncertain */
export const LOW_CONFIDENCE = 0.3;

/** Default confidence when score is not available */
export const DEFAULT_CONFIDENCE = 0.5;

/**
 * Get confidence level label for a given confidence score
 */
export function getConfidenceLevel(confidence: number): 'high' | 'medium' | 'low' {
  if (confidence >= HIGH_CONFIDENCE) return 'high';
  if (confidence >= MEDIUM_CONFIDENCE) return 'medium';
  return 'low';
}

/**
 * Get confidence color for UI display
 */
export function getConfidenceColor(confidence: number): 'success' | 'warning' | 'error' {
  if (confidence >= HIGH_CONFIDENCE) return 'success';
  if (confidence >= MEDIUM_CONFIDENCE) return 'warning';
  return 'error';
}

// ============================================================================
// HEADER FIELD CATEGORIES (289 fields)
// ============================================================================

export interface FieldCategory {
  id: string;
  label: string;
  icon?: string;
  fields: string[];
}

/**
 * ML field name to ATLAS form path mapping
 * ML format: empfng_name → ATLAS format: kopf.empfanger.name
 */
export const ML_TO_ATLAS_HEADER_MAPPING: Record<string, string> = {
  // Declaration Info
  'dienst_no': 'kopf.dienststellennummer',  // Customs Office Number (NOT LRN!)
  'kopf_art_der_aus': 'kopf.artderAusfuhranmeldung',  // "aus" = Ausfuhranmeldung (Export Declaration Type); matches backend ml_field_mapper. Prevents overwriting artderAnmeldung (Declaration Type, e.g. "EX").
  'kopf_art_der_uberfuh': 'kopf.artderUberfuhrung',
  'kopf_atlas_version': 'kopf.atlasVersion',
  'kopf_bestiland': 'kopf.bestimmungsLand',
  'kopf_zeit_der_anmeld': 'kopf.zeitpunktderAnmeldung',
  'geschaftsvorgang_art': 'kopf.artdesGeschafts',
  'kopf_bet_konstell': 'kopf.beteiligtenKonstellation',
  'kopf_container': 'kopf.containerIndikator',
  'kopf_ant_auf_ausstat': 'kopf.antragAufAusstattung',
  'kopf_gesamt_rohmasse': 'kopf.gesamtRohmasse',
  'kopf_regnum_fremdsys': 'kopf.registriernummerExtern',
  'kopf_vermerk': 'kopf.vermerk',
  'kopf_bezugnum': 'kopf.bezugsnummer',
  'kopf_datum': 'kopf.datum',
  'kopf_anzahl_pos': 'kopf.anzahlPositionen',
  'kopf_bin': 'kopf.bin',
  'kopf_bewillinum_aus': 'kopf.bewilligungsnummerAusfuhrer',
  'kopf_ausfuhrland': 'kopf.ausfuhrland',
  'kopf_besondere_umstande': 'kopf.besondereUmstande',
  'kopf_beford_zahlungweise': 'kopf.beforderungskostenZahlungsart',
  'kopf_ken_der_sendung': 'kopf.kennungDerSendung',
  'kopf_mrn': 'kopf.mrn',
  'create_date': 'kopf.erstelldatum',
  'delete_status': 'kopf.deleteStatus',
  'masgebliches_datum': 'kopf.massgeblichesDatum',
  'datum_des_ausgangs': 'kopf.kopfDatumdesAusgangs',
  'statistische_meldung': 'kopf.statistischeMeldung',
  'adressaten_konstellation': 'kopf.adressatenKonstellation',
  'sicherheit': 'kopf.sicherheit',
  'eori_number': 'kopf.eoriNiederlassungsnummer',
  'art_der_anm_verfahren': 'kopf.artDerAnmeldungVerfahren',

  // Loading Location
  'ladeort_code': 'kopf.ladeort.code',
  'ladeort_strasse': 'kopf.ladeort.strasse',
  'ladeort_plz': 'kopf.ladeort.plz',
  'ladeort_ort': 'kopf.ladeort.ort',
  'ladeort_zusatz': 'kopf.ladeort.zusatz',

  // Customs Offices
  'ausfuhzolllstelle_dienst': 'kopf.ausfuhrzollstelleDienststellennummer',
  'aus_fur_die_eam_dienst': 'kopf.ausfuhrZollstelleFurDieErganzende',
  'vorge_ausgang_dienst': 'kopf.vorgeseheneAusgangszollstelleDienststellennummer',
  'tatsachlicheAusgangsDienst': 'kopf.tatsachlicheAusgangszollstelle',
  'tatsachliche_ausgangszollstelle_dienst': 'kopf.tatsachlicheAusgangszollstelleDienst',
  'gestellungszollstelle': 'kopf.gestellungszollstelle',

  // Seals
  'verschluss_zeichen': 'kopf.verschlussZeichen',
  'verschlusse_art': 'kopf.verschlussArt',
  'versch_anzahl': 'kopf.verschlussAnzahl',

  // Transport - Inland
  'beford_im_Inland_verk': 'kopf.inlandischerVerkehrszweig',

  // Transport - Border
  'beford_an_der_gren_verk': 'kopf.verkehrszweigAnDerGrenze',
  'beford_an_der_gren_art': 'kopf.beforderungsmittelderGrenzeArt',
  'beford_an_der_gren_kenn': 'kopf.beforderungsmittelderGrenzeKennzeichen',
  'beford_an_der_gren_staat': 'kopf.beforderungsmittelderGrenzeStaatszugehorigkeit',

  // Transport - Departure
  'beford_am_abgang_art': 'kopf.beforderungsmittelBeimAbgang.art',
  'beford_am_abgang_kenn': 'kopf.beforderungsmittelBeimAbgang.kennzeichen',
  'beford_am_abga_staats_keit': 'kopf.beforderungsmittelBeimAbgang.staatszugehorigkeit',

  // Presentation
  'gestell_ver_und_ver_anfang': 'kopf.gestellungVerpackungAnfang',
  'gestell_verpack_und_ver_ende': 'kopf.gestellungVerpackungEnde',

  // Dezentraler Partner
  'dezentraler_kpartner_tin': 'kopf.dezentralerKpartnerTin',

  // Ausfuhrer (Exporter)
  'ausfuh_tin': 'kopf.ausfuhrer.tin',
  'ausfuh_deutsche_zollnum': 'kopf.ausfuhrer.deutscheZollnummer',
  'ausfuh_name': 'kopf.ausfuhrer.name',
  'ausfuh_stra_hnum': 'kopf.ausfuhrer.strasse',
  'ausfuh_ort': 'kopf.ausfuhrer.ort',
  'ausfuh_plz_ort': 'kopf.ausfuhrer.plz',
  'ausfuh_land': 'kopf.ausfuhrer.land',
  'ausfuh_tin_check': 'kopf.ausfuhrer.tinCheck',
  'ausfuh_tin_check2': 'kopf.ausfuhrer.tinCheck2',
  'ausfuh_identifikationsart': 'kopf.ausfuhrer.identifikationsart',
  'ausfuh_niederlassungsnummer': 'kopf.ausfuhrer.niederlassungsNummer',
  'ausfuh_ansprech_stell': 'kopf.ausfuhrer.ansprechpartnerStelle',
  'ausfuh_ansprech_sach': 'kopf.ausfuhrer.ansprechpartnerName',
  'ausfuh_ansprech_telefon': 'kopf.ausfuhrer.ansprechpartnerTelefon',
  'ausfuh_ansprech_telefax': 'kopf.ausfuhrer.ansprechpartnerFax',
  'ausfuh_ansprech_email': 'kopf.ausfuhrer.ansprechpartnerEmail',
  'ausfuh_beauft_name': 'kopf.ausfuhrer.beauftragterName',
  'ausfuhr_textliche_erklar': 'kopf.ausfuhrer.textlicheErklarung',
  'ausfuhr_antragsart': 'kopf.ausfuhrer.antragsart',
  'ausfuhr_zahlungsweg': 'kopf.ausfuhrer.zahlungsweg',
  'ausfuhr_zustell_macht': 'kopf.ausfuhrer.zustellMacht',
  'ausfuhrer_sicher_konto': 'kopf.ausfuhrer.sicherheitskonto',
  'ausfuhr_klamm_vorbehalt': 'kopf.ausfuhrer.klammerVorbehalt',

  // Empfanger (Recipient)
  'empfng_tin': 'kopf.empfanger.tin',
  'empfng_name': 'kopf.empfanger.name',
  'empfng_stra_hnum': 'kopf.empfanger.strasse',
  'empfng_ort': 'kopf.empfanger.ort',
  'empfng_plz_ort': 'kopf.empfanger.plz',
  'empfng_land': 'kopf.empfanger.land',
  'empfng_identifikationsart': 'kopf.empfanger.identifikationsart',
  'empfng_niederlassungsnummer': 'kopf.empfanger.niederlassungsNummer',

  // Anmelder (Declarant)
  'anmeld_tin': 'kopf.anmelder.tin',
  'anmeld_deutsche_zollnum': 'kopf.anmelder.deutscheZollnummer',
  'anmeld_name': 'kopf.anmelder.name',
  'anmeld_stra_hnum': 'kopf.anmelder.strasse',
  'anmeld_ort': 'kopf.anmelder.ort',
  'anmeld_plz_ort': 'kopf.anmelder.plz',
  'anmeld_land': 'kopf.anmelder.land',
  'anmeld_beauft_stellung': 'kopf.anmelder.beauftragterStellung',
  'anmeld_beauft_name': 'kopf.anmelder.beauftragterName',
  'anmeld_tin_check': 'kopf.anmelder.tinCheck',
  'anmeld_tin_check2': 'kopf.anmelder.tinCheck2',
  'anmled_identifikationsart': 'kopf.anmelder.identifikationsart',
  'anmled_niederlassungsnummer': 'kopf.anmelder.niederlassungsNummer',
  'anmeld_ansprech_sach': 'kopf.anmelder.ansprechpartnerName',
  'anmeld_ansprech_telefon': 'kopf.anmelder.phone',
  'anmeld_ansprech_telefax': 'kopf.anmelder.fax',
  'anmeld_ansprech_email': 'kopf.anmelder.email',

  // Vertreter (Representative)
  'vert_desa_anmeld_tin': 'kopf.vertreter.tin',
  'vert_des_anmeld_zollnum': 'kopf.vertreter.deutscheZollnummer',
  'vert_des_anmeld_n': 'kopf.vertreter.name',
  'vert_des_anmeld_hnum': 'kopf.vertreter.strasse',
  'vert_des_anmeld_ort': 'kopf.vertreter.ort',
  'vert_des_anmeld_plz_ort': 'kopf.vertreter.plz',
  'vert_beauft_stellung': 'kopf.vertreter.beauftragterStellung',
  'vert_beauft_name': 'kopf.vertreter.beauftragterName',
  'vert_desa_anmeld_tin_check': 'kopf.vertreter.tinCheck',
  'vert_desa_anmeld_tin_check2': 'kopf.vertreter.tinCheck2',
  'vert_des_identifikationsart': 'kopf.vertreter.identifikationsart',
  'vert_des_niederlassungsnummer': 'kopf.vertreter.niederlassungsNummer',
  'vertre_ansprech_sach': 'kopf.vertreter.ansprechpartnerName',
  'vertre_ansprech_telefon': 'kopf.vertreter.telefon',
  'vertre_ansprech_telefax': 'kopf.vertreter.fax',
  'vertre_ansprech_email': 'kopf.vertreter.email',

  // Subunternehmer (Subcontractor)
  'subunt_tin': 'kopf.subunternehmer.tin',
  'subunt_zollnum': 'kopf.subunternehmer.deutscheZollnummer',
  'subunt_nam': 'kopf.subunternehmer.name',
  'subunt_hnum': 'kopf.subunternehmer.strasse',
  'subunt_ort': 'kopf.subunternehmer.ort',
  'subunt_plz_ort': 'kopf.subunternehmer.plz',
  'subunt_land': 'kopf.subunternehmer.land',
  'subunt_beauft_stellung': 'kopf.subunternehmer.beauftragterStellung',
  'subunt_beauft_name': 'kopf.subunternehmer.beauftragterName',
  'subunt_tin_check': 'kopf.subunternehmer.tinCheck',
  'subunt_tin_check2': 'kopf.subunternehmer.tinCheck2',
  'subunt_identifikationsart': 'kopf.subunternehmer.identifikationsart',
  'subunt_niederlassungsnummer': 'kopf.subunternehmer.niederlassungsNummer',
  'subunt_ansprech_sach': 'kopf.subunternehmer.ansprechpartnerName',
  'subunt_ansprech_telefon': 'kopf.subunternehmer.telefon',
  'subunt_ansprech_telefax': 'kopf.subunternehmer.fax',
  'subunt_ansprech_email': 'kopf.subunternehmer.email',

  // Lieferung (Delivery)
  'lieferung_Incoterm_code': 'kopf.lieferbedingungIncotermCode',
  'liebdigng_ort': 'kopf.lieferbedingungOrt',
  'liebdigng_text_teil': 'kopf.lieferbedingungText',
  'liebdigng_unlocode': 'kopf.lieferbedingungUnLocode',
  'liebdigng_land': 'kopf.lieferbedingungLand',

  // Geschaftsvorgang (Transaction)
  'gschftsvrgng_rechnungs': 'kopf.inRechnunggestellterGesamtbetrag',
  'gschftsvrgng_wahrung': 'kopf.rechnungswahrung',

  // Bewilligung (Authorization)
  'bewilg_anschreibeverfahren': 'kopf.bewilligungAnschreibeverfahren',
  'bewilg_passive_veredelung': 'kopf.bewilligungPassiveVeredelung',
  'bewlg_Vertrauenswurdiger_aus': 'kopf.bewilligungVertrauenswurdigerAusfuhrer',
  'bewlg_zugelassener_aus': 'kopf.bewilligungZugelassenerAusfuhrer',

  // Endverwerder (End User)
  'endverwer_identifikationsart': 'kopf.endverwerder.identifikationsart',
  'endverwer_tin': 'kopf.endverwerder.tin',
  'endverwer_niederlassungsnummer': 'kopf.endverwerder.niederlassungsNummer',
  'endverwer_name': 'kopf.endverwerder.name',
  'endverwer_strase_hausnum': 'kopf.endverwerder.strasse',
  'endverwer_ort': 'kopf.endverwerder.ort',
  'endverwer_plz': 'kopf.endverwerder.plz',
  'endverwer_land': 'kopf.endverwerder.land',

  // Passive Veredelung
  'passiveVerdDatumDerWiederin': 'kopf.passiveVeredelung.datumWiedereinfuhr',
  'passive_verd_standardaustausch': 'kopf.passiveVeredelung.standardaustausch',
  'passive_verd_datum_der_wiederin': 'kopf.passiveVeredelung.datumDerWiedereinfuhr',
  'wiedereinfuhr_land': 'kopf.passiveVeredelung.wiedereinfuhrLand',

  // Namlichkeitsmittel (Identity Means)
  'namlichkeitsmittel_art': 'kopf.namlichkeitsmittel.art',
  'namlichkeitTextlicheBesch': 'kopf.namlichkeitsmittel.textlicheBeschreibung',
  'namlichkeitsmittel_textliche_besch': 'kopf.namlichkeitsmittel.textlicheBeschreibung',

  // Erzeugnis (Product)
  'erzeugnis_warennummer': 'kopf.erzeugnis.warennummer',
  'erzeugnis_warenbezeichnung': 'kopf.erzeugnis.warenbezeichnung',

  // Versender (Sender)
  'versender_land': 'kopf.versender.land',
  'versender_ort': 'kopf.versender.ort',
  'versender_plz': 'kopf.versender.plz',
  'versender_strasse': 'kopf.versender.strasse',
  'versender_name': 'kopf.versender.name',
  'versender_nied': 'kopf.versender.niederlassungsNummer',
  'versender_tin': 'kopf.versender.tin',

  // Beforderer (Carrier)
  'beforderer_nied': 'kopf.beforderer.niederlassungsNummer',
  'beforderer_tin': 'kopf.beforderer.tin',

  // Aussen Ausfuhrer (Foreign Exporter)
  'aussen_ausfuhrer_land': 'kopf.aussenAusfuhrer.land',
  'aussen_ausfuhrer_ort': 'kopf.aussenAusfuhrer.ort',
  'aussen_ausfuhrer_plz': 'kopf.aussenAusfuhrer.plz',
  'aussen_ausfuhrer_strasse': 'kopf.aussenAusfuhrer.strasse',
  'aussen_ausfuhrer_name': 'kopf.aussenAusfuhrer.name',
  'aussen_ausfuhrer_nied': 'kopf.aussenAusfuhrer.niederlassungsNummer',
  'aussen_ausfuhrer_tin': 'kopf.aussenAusfuhrer.tin',

  // Warenort (Location of Goods)
  'warenort_artdesortes': 'kopf.warenortArtdesOrtes',
  'warenort_ortsbestimung': 'kopf.warenortArtDerOrtsbestimmung',
  'warenort_bewillignummr': 'kopf.warenortBewilligungsnummer',
  'warenort_unlocode': 'kopf.warenortUnLocode',
  'warenort_gnssbreite': 'kopf.warenortGnssBreite',
  'warenort_gnsslang': 'kopf.warenortGnssLange',
  'warenort_addr_land': 'kopf.warenortAdresse.land',
  'warenort_ans_name': 'kopf.warenortAnsprechpartner.name',
  'warenort_telfon': 'kopf.warenortAnsprechpartner.telefon',
  'warenort_email': 'kopf.warenortAnsprechpartner.email',

  // Beforderungsroute (Transport Route)
  'beforderungsroute_land': 'kopf.beforderungsroute.ausgewahlteLander',

  // Nachrichten (Messages)
  'nachrich_szen_nummer': 'kopf.nachricht.szenarioNummer',
  'nachrich_szen_hinwe': 'kopf.nachricht.hinweis',

  // Tatbestand (Facts)
  'tatbestand_kennzeichen': 'kopf.tatbestandKennzeichen',

  // Internal
  'intern_info': 'kopf.internInfo',
  'sent_identifier': 'kopf.sentIdentifier',

  // Verfahrensinhaber PV
  'verfrnpv_tin': 'kopf.verfahrensinhaberPV.tin',
  'verfrnpv_beauft_stellung': 'kopf.verfahrensinhaberPV.beauftragterStellung',
  'verfrnpv_ansprech_sach': 'kopf.verfahrensinhaberPV.ansprechpartnerName',
  'verfrnpv_ansprech_telefon': 'kopf.verfahrensinhaberPV.telefon',
  'verfrnpv_ansprech_telefax': 'kopf.verfahrensinhaberPV.fax',
  'verfrnpv_ansprech_email': 'kopf.verfahrensinhaberPV.email',
  'verfrnpv_niedersnummer': 'kopf.verfahrensinhaberPV.niederlassungsNummer',
  'verfrnpv_identktionsart': 'kopf.verfahrensinhaberPV.identifikationsart',
};

/**
 * ML field name to ATLAS form path mapping for POSITION fields
 */
export const ML_TO_ATLAS_POSITION_MAPPING: Record<string, string> = {
  // Ware (Goods) Basic Info
  'ware_no': 'warePositionsnummer',
  'ware_warenum_kn8': 'wareWarennummerKN8',
  'ware_warenum_taric': 'wareTarifnummer',
  'ware_warenum_erst_zus': 'wareErsteZusatznummer',
  'ware_warenum_zwe_zus': 'wareZweiteZusatznummer',
  'ware_warenum_nat_ang': 'wareNationaleAngabe',
  'ware_warenbe': 'wareWarenbezeichnung',
  'ware_regnum_fresys': 'registriernummerextern',
  'ware_vermerk': 'wareVermerk',
  'ware_ursprungsbland': 'ursprungsland',
  'ware_egmasse': 'wareEigenmasse',
  'ware_rohmasse': 'wareRohmasse',
  'ware_kenn_der_sendung': 'wareKennungDerSendung',
  'ware_gefahrgutnummer': 'wareGefahrgutnummer',
  'ware_beford_zahlung': 'wareBeforderungZahlung',
  'ware_cus_nummer': 'warecusnummer',

  // Verfahren (Procedures)
  'verfah_angemelde': 'verfahren',
  'verfah_vorangegang': 'vorangegangenesVerfahren',
  'verfah_weiteres': 'zusatzlichesVerfahren',
  'verfah_ausfuhrstatt': 'verfahrenAusfuhrstatt',

  // Aubenhandelsstatistik (Foreign Trade Statistics)
  'aubenhandelsstat_menge': 'statistischeMenge',
  'aubenhandelstat_wert': 'statistischerWert',

  // Packaging
  'pack_anzahl': 'verpackung.anzahlderPackstucke',
  'pack_num': 'verpackung.packstuckNummer',
  'pack_verpackungsrt': 'verpackung.artderVerpackung',
  'pack_zeich_num_teil1': 'verpackung.versandzeichen',
  'pack_zeich_num_teil2': 'verpackung.versandzeichenTeil2',

  // Container
  'container_num': 'containerNummer',

  // Vorpapiere (Previous Documents)
  'vorpapiere_typ': 'vorpapiereTyp',
  'vorpapier_referenz': 'vorpapiereReferenz',
  'vorpapier_zusatz': 'vorpapiereZusatz',
  'vorp_type': 'vorpapiereTypAlt',

  // Unterlage (Supporting Documents)
  'unterlage_typ': 'unterlage.art',
  'unterlage_ref': 'unterlage.referenznummer',
  'unterlage_datum_der_ausstell': 'unterlage.datumderausstellung',
  'unterlage_datum_des_gultig': 'unterlage.geltungsdauer',
  'unlage_qualifiz': 'unterlage.qualifikator',
  'unlage_zusatz': 'unterlage.zusatz',
  'unterlage_detail': 'unterlage.detail',
  'unterlage_mabein': 'unterlage.masseinheit',
  'unterlage_abschr_menge': 'unterlage.abschreibungsmenge',
  'unterlage_wert': 'unterlage.wert',

  // Sonstiger Verweis (Other Reference)
  'p_son_verweis_art': 'sonstigerVerweisTyp',  // Other Reference Type
  
  // Besondere Vermerke (Special Notes)
  'bes_vermerke_aus': 'besondereVermerkeAusfuhr',

  // Ausfuhr Position (Export Position)
  'ausfuhr_waren_erste_ergan': 'ausfuhrWarenErsteErganzung',
  'ausfuhr_waren_zweite_ergan': 'ausfuhrWarenZweiteErganzung',
  'ausfuhr_ursprungsland': 'ausfuhrUrsprungsland',
  'ausfuhr_schatzwert': 'ausfuhrSchatzwert',
  'ausfuhr_menge': 'ausfuhrMenge',
  'ausfuhr_anpassung': 'ausfuhrAnpassung',
  'ausfuhr_anteilswert_a': 'ausfuhrAnteilswertA',
  'ausfuhr_anteilswert_b': 'ausfuhrAnteilswertB',
  'ausfuhr_anteilswert_c': 'ausfuhrAnteilswertC',
  'ausfuhr_anteilswert_d': 'ausfuhrAnteilswertD',
  'ausfuhr_zahlung_nai': 'ausfuhrZahlungNai',
  'ausfrLand': 'ausfuhrLand',
  'ausfuhrerstattung_menge': 'ausfuhrerstattungMenge',

  // Beendig ZL (Termination ZL)
  'beendig_zl_anzahl_pos': 'beendigZlAnzahlPos',
  'beendig_zl_bewill': 'beendigZlBewilligung',
  'bewill_zl_bezug': 'bewilligungZlBezug',
  'position_zl_sat_nummer': 'positionZlSatzNummer',
  'position_zl_pos_nummer': 'positionZlPositionsNummer',
  'position_zl_reg_nummer': 'positionZlRegistriernummer',
  'position_zl_waren': 'positionZlWaren',
  'position_zl_kenn_zugang': 'positionZlKennzeichenZugang',
  'position_zl_kenn_behand': 'positionZlKennzeichenBehandlung',
  'position_zl_zusatz': 'positionZlZusatz',

  // Beendig AV UV
  'beendig_av_uv_bewillig': 'beendigAvUvBewilligung',
  'beendig_av_uv_anzahl_pos': 'beendigAvUvAnzahlPos',
  'position_av_uv_satz': 'positionAvUvSatz',
  'position_av_uv_position': 'positionAvUvPosition',
  'position_av_uv_registr': 'positionAvUvRegistriernummer',
  'position_av_uv_kennz_zug': 'positionAvUvKennzeichenZugang',
  'position_av_uv_waren_angab': 'positionAvUvWarenAngabe',

  // Empfanger Position (Recipient at Position Level)
  'empfng_tin_pos': 'empfangerPosition.tin',
  'empfng_name_pos': 'empfangerPosition.name',
  'empfng_stra_hnum_pos': 'empfangerPosition.strasse',
  'empfng_ort_pos': 'empfangerPosition.ort',
  'empfng_plz_ort_pos': 'empfangerPosition.plz',
  'empfng_land_pos': 'empfangerPosition.land',
  'empfng_identifikationsart': 'empfangerPosition.identifikationsart',
  'empfng_niederlassungsnummer': 'empfangerPosition.niederlassungsNummer',

  // Endverwerder Position (End User at Position Level)
  'endverwer_identifikationsart': 'endverwerderPosition.identifikationsart',
  'endverwer_tin': 'endverwerderPosition.tin',
  'endverwer_niederlassungsnummer': 'endverwerderPosition.niederlassungsNummer',
  'endverwer_name': 'endverwerderPosition.name',
  'endverwer_strase_hausnum': 'endverwerderPosition.strasse',
  'endverwer_ort': 'endverwerderPosition.ort',
  'endverwer_plz': 'endverwerderPosition.plz',
  'endverwer_land': 'endverwerderPosition.land',

  // Lieferbedingung Position (Delivery Terms)
  'lieferbedingung_incoterm_code': 'lieferbedingungIncotermCode',
  'lieferbedingung_text': 'lieferbedingungText',
  'lieferbedingung_ort': 'lieferbedingungOrt',
  'geschaftsvorgang_art': 'artdesGeschafts',

  // Inhaltsstoff (Ingredient)
  'inhaltsstoff_nummer': 'inhaltsstoffNummer',
  'inhalts_umrech_faktor1': 'inhaltsstoffUmrechnungsfaktor1',
  'inhalts_ken_umrech_fakt1': 'inhaltsstoffKennUmrechnungsfaktor1',
  'inhalts_umrech_faktor2': 'inhaltsstoffUmrechnungsfaktor2',
  'inhalts_umrech_fakt_satz': 'inhaltsstoffUmrechnungsfaktorSatz',
  'inhalts_anteil_ght': 'inhaltsstoffAnteilGewicht',
  'inhalts_anteilsmenge': 'inhaltsstoffAnteilsmenge',
  'inhalts_herst_erklar': 'inhaltsstoffHerstellerklarung',
  'inhalts_schlus_nummer': 'inhaltsstoffSchlussnummer',
  'inhalts_lizenznummer': 'inhaltsstoffLizenznummer',
  'inhalts_text_erklarun': 'inhaltsstoffTextErklarung',

  // Abgangsmenge (Departure Quantity)
  'abgangs_qualifikat': 'abgangsmengeQualifikator',
  'abgangsmenge_mabein': 'abgangsmengeMasseinheit',
  'abgangsmenge_wert': 'abgangsmengeWert',

  // Handelsmenge (Trade Quantity)
  'handelsmenge_qualifikat': 'handelsmengeQualifikator',
  'handelsmenge_mabein': 'handelsmengeMasseinheit',
  'handelsmenge_ert': 'handelsmengeWert',

  // Praferenz (Preference)
  'praeferenzberech': 'praferenzCode',
  'praeferenzberechtigt_list': 'praferenzberechtigtListe',

  // Addressees
  'adressaten_konstellation': 'adressatenKonstellation',
  'addressids': 'addressIds',

  // Versender Position (Sender at Position Level)
  'pos_versender_land': 'versenderPosition.land',
  'pos_versender_ort': 'versenderPosition.ort',
  'pos_versender_plz': 'versenderPosition.plz',
  'pos_versender_strasse': 'versenderPosition.strasse',
  'pos_versender_name': 'versenderPosition.name',
  'pos_versender_nied': 'versenderPosition.niederlassungsNummer',
  'pos_versender_tin': 'versenderPosition.tin',

  // Wiedereinfuhr (Re-import)
  'datum_der_wiedereinfuhr': 'datumDerWiedereinfuhr',
  'standard_ersatzwarenverkehr': 'standardErsatzwarenverkehr',
  'ursprungsland': 'ursprungsland',

  // Bewilligung Position
  'vereinf_erte_bewilg': 'vereinfacherteBewilligung',
  'bewilligung_art': 'bewilligungArt',
  'uberwach_zoll_ref': 'uberwachungsZollReferenz',
  'zol_bewilligung_art': 'zollBewilligungArt',

  // Packstruck (Package Reference)
  'packstruckHauptpack': 'packstuckHauptpack',
};

// ============================================================================
// HEADER CATEGORIES (organized by functional area)
// ============================================================================

export const HEADER_CATEGORIES: FieldCategory[] = [
  {
    id: 'declaration',
    label: 'Declaration Info',
    icon: 'Description',
    fields: [
      'dienst_no', 'kopf_art_der_aus', 'kopf_art_der_uberfuh', 'kopf_atlas_version',
      'kopf_zeit_der_anmeld', 'geschaftsvorgang_art', 'kopf_bet_konstell',
      'kopf_container', 'kopf_ant_auf_ausstat', 'kopf_gesamt_rohmasse',
      'kopf_regnum_fremdsys', 'kopf_vermerk', 'kopf_bezugnum', 'kopf_datum',
      'kopf_anzahl_pos', 'kopf_bin', 'kopf_mrn', 'create_date', 'delete_status',
      'masgebliches_datum', 'datum_des_ausgangs', 'statistische_meldung',
      'adressaten_konstellation', 'eori_number', 'art_der_anm_verfahren',
      'sicherheit', 'kopf_besondere_umstande'
    ]
  },
  {
    id: 'ausfuhrer',
    label: 'Exporter (Ausfuhrer)',
    icon: 'Business',
    fields: [
      'ausfuh_tin', 'ausfuh_deutsche_zollnum', 'ausfuh_name', 'ausfuh_stra_hnum',
      'ausfuh_ort', 'ausfuh_plz_ort', 'ausfuh_land', 'ausfuh_tin_check',
      'ausfuh_tin_check2', 'ausfuh_identifikationsart', 'ausfuh_niederlassungsnummer',
      'ausfuh_ansprech_stell', 'ausfuh_ansprech_sach', 'ausfuh_ansprech_telefon',
      'ausfuh_ansprech_telefax', 'ausfuh_ansprech_email', 'ausfuh_beauft_name',
      'ausfuhr_textliche_erklar', 'ausfuhr_antragsart', 'ausfuhr_zahlungsweg',
      'ausfuhr_zustell_macht', 'ausfuhrer_sicher_konto', 'ausfuhr_klamm_vorbehalt'
    ]
  },
  {
    id: 'empfanger',
    label: 'Recipient (Empfanger)',
    icon: 'Person',
    fields: [
      'empfng_tin', 'empfng_name', 'empfng_stra_hnum', 'empfng_ort',
      'empfng_plz_ort', 'empfng_land', 'empfng_identifikationsart',
      'empfng_niederlassungsnummer'
    ]
  },
  {
    id: 'anmelder',
    label: 'Declarant (Anmelder)',
    icon: 'Badge',
    fields: [
      'anmeld_tin', 'anmeld_deutsche_zollnum', 'anmeld_name', 'anmeld_stra_hnum',
      'anmeld_ort', 'anmeld_plz_ort', 'anmeld_land', 'anmeld_beauft_stellung',
      'anmeld_beauft_name', 'anmeld_tin_check', 'anmeld_tin_check2',
      'anmled_identifikationsart', 'anmled_niederlassungsnummer',
      'anmeld_ansprech_sach', 'anmeld_ansprech_telefon', 'anmeld_ansprech_telefax',
      'anmeld_ansprech_email'
    ]
  },
  {
    id: 'vertreter',
    label: 'Representative (Vertreter)',
    icon: 'SupportAgent',
    fields: [
      'vert_desa_anmeld_tin', 'vert_des_anmeld_zollnum', 'vert_des_anmeld_n',
      'vert_des_anmeld_hnum', 'vert_des_anmeld_ort', 'vert_des_anmeld_plz_ort',
      'vert_beauft_stellung', 'vert_beauft_name', 'vert_desa_anmeld_tin_check',
      'vert_desa_anmeld_tin_check2', 'vert_des_identifikationsart',
      'vert_des_niederlassungsnummer', 'vertre_ansprech_sach',
      'vertre_ansprech_telefon', 'vertre_ansprech_telefax', 'vertre_ansprech_email'
    ]
  },
  {
    id: 'subunternehmer',
    label: 'Subcontractor (Subunternehmer)',
    icon: 'Engineering',
    fields: [
      'subunt_tin', 'subunt_zollnum', 'subunt_nam', 'subunt_hnum', 'subunt_ort',
      'subunt_plz_ort', 'subunt_land', 'subunt_beauft_stellung', 'subunt_beauft_name',
      'subunt_tin_check', 'subunt_tin_check2', 'subunt_identifikationsart',
      'subunt_niederlassungsnummer', 'subunt_ansprech_sach', 'subunt_ansprech_telefon',
      'subunt_ansprech_telefax', 'subunt_ansprech_email'
    ]
  },
  {
    id: 'lieferung',
    label: 'Delivery Terms',
    icon: 'LocalShipping',
    fields: [
      'kopf_bestiland', 'kopf_ausfuhrland', 'lieferung_Incoterm_code', 'liebdigng_ort',
      'liebdigng_text_teil', 'liebdigng_unlocode', 'liebdigng_land',
      'gschftsvrgng_rechnungs', 'gschftsvrgng_wahrung'
    ]
  },
  {
    id: 'transport',
    label: 'Transport',
    icon: 'DirectionsCar',
    fields: [
      'beford_im_Inland_verk', 'beford_an_der_gren_verk', 'beford_an_der_gren_art',
      'beford_an_der_gren_kenn', 'beford_an_der_gren_staat', 'beford_am_abgang_art',
      'beford_am_abgang_kenn', 'beford_am_abga_staats_keit', 'beforderungsroute_land',
      'kopf_beford_zahlungweise'
    ]
  },
  {
    id: 'customs_offices',
    label: 'Customs Offices',
    icon: 'AccountBalance',
    fields: [
      'ausfuhzolllstelle_dienst', 'aus_fur_die_eam_dienst', 'vorge_ausgang_dienst',
      'tatsachlicheAusgangsDienst', 'tatsachliche_ausgangszollstelle_dienst',
      'gestellungszollstelle'
    ]
  },
  {
    id: 'ladeort',
    label: 'Loading Location',
    icon: 'Place',
    fields: [
      'ladeort_code', 'ladeort_strasse', 'ladeort_plz', 'ladeort_ort', 'ladeort_zusatz'
    ]
  },
  {
    id: 'warenort',
    label: 'Location of Goods',
    icon: 'Warehouse',
    fields: [
      'warenort_artdesortes', 'warenort_ortsbestimung', 'warenort_bewillignummr',
      'warenort_unlocode', 'warenort_gnssbreite', 'warenort_gnsslang',
      'warenort_addr_land', 'warenort_ans_name', 'warenort_telfon', 'warenort_email'
    ]
  },
  {
    id: 'seals',
    label: 'Seals & Security',
    icon: 'Lock',
    fields: [
      'verschluss_zeichen', 'verschlusse_art', 'versch_anzahl',
      'gestell_ver_und_ver_anfang', 'gestell_verpack_und_ver_ende'
    ]
  },
  {
    id: 'bewilligung',
    label: 'Authorizations',
    icon: 'VerifiedUser',
    fields: [
      'kopf_bewillinum_aus', 'bewilg_anschreibeverfahren', 'bewilg_passive_veredelung',
      'bewlg_Vertrauenswurdiger_aus', 'bewlg_zugelassener_aus'
    ]
  },
  {
    id: 'endverwerder',
    label: 'End User',
    icon: 'PersonPin',
    fields: [
      'endverwer_identifikationsart', 'endverwer_tin', 'endverwer_niederlassungsnummer',
      'endverwer_name', 'endverwer_strase_hausnum', 'endverwer_ort',
      'endverwer_plz', 'endverwer_land'
    ]
  },
  {
    id: 'passive_veredelung',
    label: 'Passive Processing',
    icon: 'Autorenew',
    fields: [
      'passiveVerdDatumDerWiederin', 'passive_verd_standardaustausch',
      'passive_verd_datum_der_wiederin', 'wiedereinfuhr_land',
      'namlichkeitsmittel_art', 'namlichkeitTextlicheBesch',
      'namlichkeitsmittel_textliche_besch', 'erzeugnis_warennummer',
      'erzeugnis_warenbezeichnung'
    ]
  },
  {
    id: 'other_parties',
    label: 'Other Parties',
    icon: 'Groups',
    fields: [
      'dezentraler_kpartner_tin', 'versender_land', 'versender_ort', 'versender_plz',
      'versender_strasse', 'versender_name', 'versender_nied', 'versender_tin',
      'beforderer_nied', 'beforderer_tin', 'aussen_ausfuhrer_land',
      'aussen_ausfuhrer_ort', 'aussen_ausfuhrer_plz', 'aussen_ausfuhrer_strasse',
      'aussen_ausfuhrer_name', 'aussen_ausfuhrer_nied', 'aussen_ausfuhrer_tin'
    ]
  },
  {
    id: 'verfahrensinhaber',
    label: 'Procedure Holder',
    icon: 'Assignment',
    fields: [
      'verfrnpv_tin', 'verfrnpv_beauft_stellung', 'verfrnpv_ansprech_sach',
      'verfrnpv_ansprech_telefon', 'verfrnpv_ansprech_telefax',
      'verfrnpv_ansprech_email', 'verfrnpv_niedersnummer', 'verfrnpv_identktionsart',
      'tatbestand_kennzeichen'
    ]
  },
  {
    id: 'nachrichten',
    label: 'Messages & Internal',
    icon: 'Message',
    fields: [
      'nachrich_szen_nummer', 'nachrich_szen_hinwe', 'intern_info', 'sent_identifier',
      'kopf_ken_der_sendung'
    ]
  }
];

// ============================================================================
// POSITION CATEGORIES (organized by functional area)
// ============================================================================

export const POSITION_CATEGORIES: FieldCategory[] = [
  {
    id: 'ware',
    label: 'Goods Info',
    icon: 'Inventory',
    fields: [
      'ware_no', 'ware_warenum_kn8', 'ware_warenum_taric', 'ware_warenum_erst_zus',
      'ware_warenum_zwe_zus', 'ware_warenum_nat_ang', 'ware_warenbe',
      'ware_regnum_fresys', 'ware_vermerk', 'ware_ursprungsbland', 'ware_egmasse',
      'ware_rohmasse', 'ware_kenn_der_sendung', 'ware_gefahrgutnummer',
      'ware_beford_zahlung', 'ware_cus_nummer'
    ]
  },
  {
    id: 'verfahren',
    label: 'Procedures',
    icon: 'Rule',
    fields: [
      'verfah_angemelde', 'verfah_vorangegang', 'verfah_weiteres', 'verfah_ausfuhrstatt'
    ]
  },
  {
    id: 'statistics',
    label: 'Statistics & Values',
    icon: 'Analytics',
    fields: [
      'aubenhandelsstat_menge', 'aubenhandelstat_wert'
    ]
  },
  {
    id: 'packaging',
    label: 'Packaging',
    icon: 'AllInbox',
    fields: [
      'pack_anzahl', 'pack_num', 'pack_verpackungsrt', 'pack_zeich_num_teil1',
      'pack_zeich_num_teil2', 'packstruckHauptpack'
    ]
  },
  {
    id: 'container',
    label: 'Container',
    icon: 'ViewInAr',
    fields: ['container_num']
  },
  {
    id: 'vorpapiere',
    label: 'Previous Documents',
    icon: 'History',
    fields: [
      'vorpapiere_typ', 'vorpapier_referenz', 'vorpapier_zusatz', 'vorp_type'
    ]
  },
  {
    id: 'unterlage',
    label: 'Supporting Documents',
    icon: 'AttachFile',
    fields: [
      'unterlage_typ', 'unterlage_ref', 'unterlage_datum_der_ausstell',
      'unterlage_datum_des_gultig', 'unlage_qualifiz', 'unlage_zusatz',
      'unterlage_detail', 'unterlage_mabein', 'unterlage_abschr_menge', 'unterlage_wert',
      'p_son_verweis_art'  // Other Reference Type
    ]
  },
  {
    id: 'ausfuhr_pos',
    label: 'Export Data',
    icon: 'FlightTakeoff',
    fields: [
      'ausfuhr_waren_erste_ergan', 'ausfuhr_waren_zweite_ergan', 'ausfuhr_ursprungsland',
      'ausfuhr_schatzwert', 'ausfuhr_menge', 'ausfuhr_anpassung',
      'ausfuhr_anteilswert_a', 'ausfuhr_anteilswert_b', 'ausfuhr_anteilswert_c',
      'ausfuhr_anteilswert_d', 'ausfuhr_zahlung_nai', 'ausfrLand', 'ausfuhrerstattung_menge',
      'bes_vermerke_aus'
    ]
  },
  {
    id: 'empfanger_pos',
    label: 'Recipient (Position)',
    icon: 'Person',
    fields: [
      'empfng_tin_pos', 'empfng_name_pos', 'empfng_stra_hnum_pos', 'empfng_ort_pos',
      'empfng_plz_ort_pos', 'empfng_land_pos', 'empfng_identifikationsart',
      'empfng_niederlassungsnummer'
    ]
  },
  {
    id: 'endverwerder_pos',
    label: 'End User (Position)',
    icon: 'PersonPin',
    fields: [
      'endverwer_identifikationsart', 'endverwer_tin', 'endverwer_niederlassungsnummer',
      'endverwer_name', 'endverwer_strase_hausnum', 'endverwer_ort',
      'endverwer_plz', 'endverwer_land'
    ]
  },
  {
    id: 'lieferbedingung_pos',
    label: 'Delivery Terms (Position)',
    icon: 'LocalShipping',
    fields: [
      'lieferbedingung_incoterm_code', 'lieferbedingung_text', 'lieferbedingung_ort',
      'geschaftsvorgang_art', 'adressaten_konstellation', 'addressids'
    ]
  },
  {
    id: 'inhaltsstoff',
    label: 'Ingredients',
    icon: 'Science',
    fields: [
      'inhaltsstoff_nummer', 'inhalts_umrech_faktor1', 'inhalts_ken_umrech_fakt1',
      'inhalts_umrech_faktor2', 'inhalts_umrech_fakt_satz', 'inhalts_anteil_ght',
      'inhalts_anteilsmenge', 'inhalts_herst_erklar', 'inhalts_schlus_nummer',
      'inhalts_lizenznummer', 'inhalts_text_erklarun'
    ]
  },
  {
    id: 'beendig_zl',
    label: 'ZL Termination',
    icon: 'EventAvailable',
    fields: [
      'beendig_zl_anzahl_pos', 'beendig_zl_bewill', 'bewill_zl_bezug',
      'position_zl_sat_nummer', 'position_zl_pos_nummer', 'position_zl_reg_nummer',
      'position_zl_waren', 'position_zl_kenn_zugang', 'position_zl_kenn_behand',
      'position_zl_zusatz'
    ]
  },
  {
    id: 'beendig_av',
    label: 'AV/UV Termination',
    icon: 'EventBusy',
    fields: [
      'beendig_av_uv_bewillig', 'beendig_av_uv_anzahl_pos', 'position_av_uv_satz',
      'position_av_uv_position', 'position_av_uv_registr', 'position_av_uv_kennz_zug',
      'position_av_uv_waren_angab'
    ]
  },
  {
    id: 'mengen',
    label: 'Quantities',
    icon: 'Scale',
    fields: [
      'abgangs_qualifikat', 'abgangsmenge_mabein', 'abgangsmenge_wert',
      'handelsmenge_qualifikat', 'handelsmenge_mabein', 'handelsmenge_ert'
    ]
  },
  {
    id: 'praeferenz',
    label: 'Preference',
    icon: 'Star',
    fields: ['praeferenzberech', 'praeferenzberechtigt_list']
  },
  {
    id: 'versender_pos',
    label: 'Sender (Position)',
    icon: 'Send',
    fields: [
      'pos_versender_land', 'pos_versender_ort', 'pos_versender_plz',
      'pos_versender_strasse', 'pos_versender_name', 'pos_versender_nied',
      'pos_versender_tin'
    ]
  },
  {
    id: 'wiedereinfuhr',
    label: 'Re-import',
    icon: 'Replay',
    fields: [
      'datum_der_wiedereinfuhr', 'standard_ersatzwarenverkehr', 'ursprungsland'
    ]
  },
  {
    id: 'bewilligung_pos',
    label: 'Authorizations (Position)',
    icon: 'VerifiedUser',
    fields: [
      'vereinf_erte_bewilg', 'bewilligung_art', 'uberwach_zoll_ref', 'zol_bewilligung_art'
    ]
  }
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all header field names (ML format)
 */
export function getAllHeaderFields(): string[] {
  return HEADER_CATEGORIES.flatMap(cat => cat.fields);
}

/**
 * Get all position field names (ML format)
 */
export function getAllPositionFields(): string[] {
  return POSITION_CATEGORIES.flatMap(cat => cat.fields);
}

/**
 * Convert ML field name to ATLAS form path
 */
export function mlToAtlasPath(mlField: string, isPosition: boolean = false): string {
  if (isPosition) {
    return ML_TO_ATLAS_POSITION_MAPPING[mlField] || mlField;
  }
  return ML_TO_ATLAS_HEADER_MAPPING[mlField] || mlField;
}

/**
 * Build reverse mapping from ATLAS to ML
 */
export const ATLAS_TO_ML_HEADER_MAPPING = Object.fromEntries(
  Object.entries(ML_TO_ATLAS_HEADER_MAPPING).map(([ml, atlas]) => [atlas, ml])
);

export const ATLAS_TO_ML_POSITION_MAPPING = Object.fromEntries(
  Object.entries(ML_TO_ATLAS_POSITION_MAPPING).map(([ml, atlas]) => [atlas, ml])
);

/**
 * Get category by field name
 */
export function getCategoryForField(mlField: string, isPosition: boolean = false): FieldCategory | undefined {
  const categories = isPosition ? POSITION_CATEGORIES : HEADER_CATEGORIES;
  return categories.find(cat => cat.fields.includes(mlField));
}

/**
 * Group predictions by category
 */
export interface PredictionsByCategory {
  category: FieldCategory;
  predictions: Array<{
    mlField: string;
    atlasPath: string;
    predictedValue: string;
    confidence: number;
    currentValue: string | null;
  }>;
}

export function groupPredictionsByCategory(
  predictions: Record<string, { value: string; confidence: number }>,
  currentData: Record<string, any>,
  isPosition: boolean = false
): PredictionsByCategory[] {
  const categories = isPosition ? POSITION_CATEGORIES : HEADER_CATEGORIES;
  const mapping = isPosition ? ML_TO_ATLAS_POSITION_MAPPING : ML_TO_ATLAS_HEADER_MAPPING;

  return categories
    .map(category => {
      const categoryPredictions = category.fields
        .filter(field => predictions[field])
        .map(mlField => {
          const atlasPath = mapping[mlField] || mlField;
          const prediction = predictions[mlField];
          return {
            mlField,
            atlasPath,
            predictedValue: prediction.value,
            confidence: prediction.confidence,
            currentValue: getNestedValue(currentData, atlasPath)
          };
        })
        .filter(p => p.predictedValue !== '[NULL]' && p.predictedValue !== '');

      return {
        category,
        predictions: categoryPredictions
      };
    })
    .filter(group => group.predictions.length > 0);
}

/**
 * Get nested value from object using dot notation path
 */
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

// Stats
export const FIELD_STATS = {
  totalHeaderFields: getAllHeaderFields().length,
  totalPositionFields: getAllPositionFields().length,
  totalFields: getAllHeaderFields().length + getAllPositionFields().length,
  headerCategories: HEADER_CATEGORIES.length,
  positionCategories: POSITION_CATEGORIES.length
};

/**
 * Reverse lookup: Given an ATLAS field path, find the corresponding ML field name
 * This is useful for finding predictions in ML API responses
 */
export const atlasToMlField = (atlasPath: string, isPosition: boolean = false): string | null => {
  const mapping = isPosition ? ML_TO_ATLAS_POSITION_MAPPING : ML_TO_ATLAS_HEADER_MAPPING;

  // Direct lookup - find ML field that maps to this ATLAS path
  for (const [mlField, atlasField] of Object.entries(mapping)) {
    if (atlasField === atlasPath) {
      return mlField;
    }
  }

  // Try without kopf prefix (form might have kopf. but mapping might not)
  const withoutKopf = atlasPath.replace(/^kopf\./, '');
  for (const [mlField, atlasField] of Object.entries(mapping)) {
    if (atlasField === withoutKopf) {
      return mlField;
    }
    // Also try the reverse - mapping has kopf. but path doesn't
    if (atlasField === `kopf.${atlasPath}`) {
      return mlField;
    }
  }

  // For position fields, strip position[n]. prefix and try again
  const positionMatch = atlasPath.match(/^position\[\d+\]\.(.+)$/);
  if (positionMatch) {
    const fieldName = positionMatch[1];
    for (const [mlField, atlasField] of Object.entries(ML_TO_ATLAS_POSITION_MAPPING)) {
      if (atlasField === fieldName || atlasField.endsWith(`.${fieldName}`)) {
        return mlField;
      }
    }
  }

  return null;
};

/**
 * Get ML field name from ATLAS path (alias for atlasToMlField)
 */
export const getMLFieldName = atlasToMlField;

/**
 * Get comprehensive field mappings for ML predictions
 */
export function getMLFieldMapping() {
  // Combine both header and position mappings
  const atlasToML = {
    ...ATLAS_TO_ML_HEADER_MAPPING,
    ...ATLAS_TO_ML_POSITION_MAPPING
  };
  
  const mlToAtlas = {
    ...ML_TO_ATLAS_HEADER_MAPPING,
    ...ML_TO_ATLAS_POSITION_MAPPING
  };
  
  return {
    atlasToML,
    mlToAtlas
  };
}
